import { useState, useEffect, useRef } from "react";
import { Loader2, Save, Upload, Image as ImageIcon, ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useCategories } from "@/context/CategoriesContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const BLANK = {
  title: "",
  category: "Новини",
  excerpt: "",
  content: "",
  image_url: "",
  image_alt: "",
  author: "Редакция",
  is_featured: false,
  is_breaking: false,
  status: "published",
  gallery: [],
};

export default function ArticleForm({ open, onClose, article, onSaved }) {
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const fileRef = useRef(null);
  const galleryRef = useRef(null);
  const { categories } = useCategories();

  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const resolveImg = (src) => (src && src.startsWith("/") ? `${backend}${src}` : src);

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Изберете файл с изображение.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Файлът е твърде голям (макс. 8 MB).");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Изображението е качено.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
    }
  };

  const onGalleryChange = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    setGalleryUploading(true);
    let added = 0;
    for (const file of files) {
      if (!file.type.startsWith("image/")) {
        toast.error(`„${file.name}“ не е изображение — пропуснато.`);
        continue;
      }
      if (file.size > 8 * 1024 * 1024) {
        toast.error(`„${file.name}“ е над 8 MB — пропуснато.`);
        continue;
      }
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd);
        setForm((f) => ({ ...f, gallery: [...(f.gallery || []), { url: data.url, alt: "" }] }));
        added += 1;
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      }
    }
    setGalleryUploading(false);
    if (added) toast.success(`Добавени ${added} снимки в галерията.`);
  };

  const setGalleryAlt = (idx) => (e) => {
    const val = e.target.value;
    setForm((f) => ({
      ...f,
      gallery: (f.gallery || []).map((g, i) => (i === idx ? { ...g, alt: val } : g)),
    }));
  };

  const removeGallery = (idx) => {
    setForm((f) => ({ ...f, gallery: (f.gallery || []).filter((_, i) => i !== idx) }));
  };

  useEffect(() => {
    if (article) {
      setForm({
        title: article.title || "",
        category: article.category || "Новини",
        excerpt: article.excerpt || "",
        content: article.content || "",
        image_url: article.image_url || "",
        image_alt: article.image_alt || "",
        author: article.author || "Редакция",
        is_featured: !!article.is_featured,
        is_breaking: !!article.is_breaking,
        status: article.status || "published",
        gallery: Array.isArray(article.gallery) ? article.gallery : [],
      });
    } else {
      setForm({ ...BLANK, category: categories[0]?.name || "Новини" });
    }
  }, [article, open, categories]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const toggle = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.checked }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.excerpt.trim() || !form.content.trim() || !form.image_url.trim()) {
      toast.error("Попълнете заглавие, резюме, съдържание и изображение.");
      return;
    }
    setSaving(true);
    try {
      if (article) {
        await api.put(`/admin/articles/${article.id}`, form);
        toast.success("Статията е обновена.");
      } else {
        await api.post("/admin/articles", form);
        toast.success("Статията е публикувана.");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20";
  const labelClass = "mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="article-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {article ? "Редактиране на статия" : "Нова статия"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div>
            <label className={labelClass}>Заглавие *</label>
            <input className={inputClass} data-testid="form-title" value={form.title} onChange={set("title")} />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelClass}>Рубрика *</label>
              <select className={inputClass} data-testid="form-category" value={form.category} onChange={set("category")}>
                {categories.map((c) => (
                  <option key={c.id || c.slug} value={c.name}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Автор</label>
              <input className={inputClass} data-testid="form-author" value={form.author} onChange={set("author")} />
            </div>
          </div>

          <div>
            <label className={labelClass}>Резюме *</label>
            <textarea rows={2} className={inputClass} data-testid="form-excerpt" value={form.excerpt} onChange={set("excerpt")} />
          </div>

          <div>
            <label className={labelClass}>Съдържание *</label>
            <textarea rows={8} className={inputClass} data-testid="form-content" value={form.content} onChange={set("content")} placeholder="Разделяйте абзаците с нов ред." />
          </div>

          <div>
            <label className={labelClass}>Изображение *</label>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
              <div
                className="relative h-32 w-full shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-50 sm:w-48"
                data-testid="form-image-preview"
              >
                {form.image_url ? (
                  <img src={resolveImg(form.image_url)} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-slate-300">
                    <ImageIcon className="h-8 w-8" />
                  </div>
                )}
                {uploading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/70">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  </div>
                )}
              </div>
              <div className="flex-1 space-y-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  data-testid="form-image-file"
                  onChange={onFileChange}
                />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  data-testid="form-image-upload-btn"
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition-colors hover:border-primary hover:text-primary disabled:opacity-60"
                >
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  {uploading ? "Качване…" : "Качи снимка"}
                </button>
                <p className="text-xs text-slate-400">
                  JPG, PNG, WEBP или GIF, до 8 MB. Или поставете директен URL:
                </p>
                <input
                  className={inputClass}
                  data-testid="form-image-url"
                  value={form.image_url}
                  onChange={set("image_url")}
                  placeholder="https://… или качете файл"
                />
              </div>
            </div>
          </div>

          <div>
            <label className={labelClass}>Alt текст (кратко описание на снимката)</label>
            <input className={inputClass} data-testid="form-image-alt" value={form.image_alt} onChange={set("image_alt")} />
          </div>

          <div className="rounded-lg border border-slate-200 p-4" data-testid="form-gallery-section">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <label className={labelClass + " mb-0"}>Галерия (слайдшоу в статията)</label>
                <p className="text-xs text-slate-400">
                  Допълнителни снимки, които се показват като слайдшоу в статията. По избор.
                </p>
              </div>
              <input
                ref={galleryRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                data-testid="form-gallery-file"
                onChange={onGalleryChange}
              />
              <button
                type="button"
                onClick={() => galleryRef.current?.click()}
                disabled={galleryUploading}
                data-testid="form-gallery-add-btn"
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition-colors hover:border-primary hover:text-primary disabled:opacity-60"
              >
                {galleryUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                {galleryUploading ? "Качване…" : "Добави снимки"}
              </button>
            </div>

            {(form.gallery || []).length > 0 && (
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {form.gallery.map((g, i) => (
                  <div key={i} className="flex gap-3 rounded-lg bg-slate-50 p-2" data-testid={`form-gallery-item-${i}`}>
                    <img
                      src={resolveImg(g.url)}
                      alt=""
                      className="h-16 w-24 shrink-0 rounded-md object-cover"
                    />
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                      <input
                        className="w-full rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 outline-none focus:border-primary"
                        placeholder="Alt / описание (по избор)"
                        data-testid={`form-gallery-alt-${i}`}
                        value={g.alt || ""}
                        onChange={setGalleryAlt(i)}
                      />
                      <button
                        type="button"
                        onClick={() => removeGallery(i)}
                        data-testid={`form-gallery-remove-${i}`}
                        className="inline-flex w-fit items-center gap-1 text-xs font-medium text-red-500 hover:text-red-700"
                      >
                        <X className="h-3.5 w-3.5" />
                        Премахни
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-6 rounded-lg bg-slate-50 p-3">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <input type="checkbox" data-testid="form-featured" checked={form.is_featured} onChange={toggle("is_featured")} className="h-4 w-4 accent-[#0055D4]" />
              Водеща новина
            </label>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <input type="checkbox" data-testid="form-breaking" checked={form.is_breaking} onChange={toggle("is_breaking")} className="h-4 w-4 accent-[#0055D4]" />
              Извънредно
            </label>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
              Статус:
              <select className="rounded border border-slate-200 px-2 py-1 text-sm" data-testid="form-status" value={form.status} onChange={set("status")}>
                <option value="published">Публикувана</option>
                <option value="draft">Чернова</option>
              </select>
            </label>
          </div>

          <div className="flex justify-end gap-3 border-t border-slate-100 pt-4">
            <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">
              Отказ
            </button>
            <button type="submit" disabled={saving} data-testid="form-save-btn" className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2 text-sm font-bold text-white hover:bg-[#0041A8] disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Запази
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
